import { useEffect, useState } from 'react';
import type { Panel, Button as IcButton, ButtonMode } from '@livepuls/types';
import {
  Plus,
  Trash2,
  Link2,
  Check,
  Pencil,
  Copy,
  ClipboardPaste,
  Sliders,
  Headphones,
  Radio as RadioIcon,
  Users,
  Megaphone,
  Cable,
  Gamepad2,
  Grid3x3,
  KeyRound,
  RefreshCw,
  ShieldCheck,
} from 'lucide-react';
import { useConfigStore } from '../store/config-store.js';
import { useUiStore } from '../store/ui-store.js';
import { useClipboardStore } from '../store/clipboard-store.js';
import { useExternalsStore } from '../store/externals-store.js';
import { useLiveStateStore } from '../store/live-state-store.js';
import { usePairingStore, type PairingInfo } from '../store/pairing-store.js';
import { api } from '../lib/api.js';
import { Button } from '../components/ui/Button.js';
import { Field } from '../components/ui/Field.js';
import { Modal } from '../components/ui/Modal.js';
import { EmptyState } from '../components/ui/EmptyState.js';
import { PanelCard } from '../components/panel/PanelCard.js';
import { ContextMenu, type ContextMenuItem } from '../components/ui/ContextMenu.js';

type AddBtnState =
  | { mode: 'create'; panelId: string; position: number; initialType?: IcButton['type'] }
  | { mode: 'edit'; button: IcButton }
  | null;

type MenuState =
  | { kind: 'button'; panelId: string; buttonId: string; x: number; y: number }
  | { kind: 'slot'; panelId: string; position: number; x: number; y: number }
  | null;

/** Erster gedrückter Button eines Panels (für die Remote-Hervorhebung). */
function firstPressed(set: ReadonlySet<string> | undefined): string | null {
  if (!set) return null;
  for (const id of set) return id;
  return null;
}

/**
 * Button-IDs des Panels `panelId`, die gerade ANGESPROCHEN werden (→ orange):
 * ein anderes Panel hält aktuell einen Intercom-Button gedrückt, der auf uns
 * zeigt. Der zugehörige „Gegen"-Button auf unserem Panel wird hervorgehoben.
 */
function computeReceiving(
  live: ReturnType<typeof useConfigStore.getState>['live'],
  pressed: Record<string, ReadonlySet<string>>,
  panelId: string,
): ReadonlySet<string> {
  const ids = new Set<string>();
  if (!live) return ids;
  const addressers = new Set<string>();
  for (const b of live.buttons) {
    if (
      b.type === 'intercom' &&
      b.targetPanelId === panelId &&
      pressed[b.sourcePanelId]?.has(b.id)
    ) {
      addressers.add(b.sourcePanelId);
    }
  }
  const panel = live.panels.find((p) => p.id === panelId);
  if (!panel) return ids;
  for (const slot of panel.layout) {
    if (!slot.buttonId) continue;
    const btn = live.buttons.find((x) => x.id === slot.buttonId);
    if (btn && btn.type === 'intercom' && addressers.has(btn.targetPanelId)) ids.add(btn.id);
  }
  return ids;
}

export function PanelsPage(): JSX.Element {
  const draft = useConfigStore((s) => s.draft);
  const live = useConfigStore((s) => s.live);
  const loadDraft = useConfigStore((s) => s.loadDraft);
  const select = useUiStore((s) => s.select);
  const clipboardButton = useClipboardStore((s) => s.button);
  const copyToClipboard = useClipboardStore((s) => s.copy);
  const pairingByPanelId = usePairingStore((s) => s.byPanelId);
  const fetchPairing = usePairingStore((s) => s.fetchAll);
  const regeneratePairing = usePairingStore((s) => s.regenerate);

  useEffect(() => {
    void fetchPairing();
  }, [fetchPairing]);

  // Live-State für den Remote-Control-Modus.
  const online = useLiveStateStore((s) => s.online);
  const pressed = useLiveStateStore((s) => s.pressed);
  const togglePress = useLiveStateStore((s) => s.togglePress);
  const sendAction = useLiveStateStore((s) => s.sendAction);

  // Im Remote-Modus ferngesetzte Pegel (pro Button-ID), nur für die Anzeige.
  const [remoteVol, setRemoteVol] = useState<Record<string, number>>({});

  // Pegel eines Buttons fernsteuern: sofort am Panel-Gerät anwenden (Socket),
  // lokal anzeigen und persistieren (Draft, für den nächsten Push).
  function setButtonVolume(panelId: string, buttonId: string, volume: number): void {
    setRemoteVol((m) => ({ ...m, [buttonId]: volume }));
    sendAction(panelId, { kind: 'button-volume', buttonId, volume });
    api.put(`/api/v1/buttons/${buttonId}`, { volume }).catch((err) => {
      console.warn('[remote-volume] persist failed', err);
    });
  }

  const [createOpen, setCreateOpen] = useState<'standard' | 'source' | null>(null);

  // Web-URL zum Öffnen eines Panels/einer Quelle (für „Öffnen"/Share).
  function panelOpenUrl(id: string, kind: 'standard' | 'source'): string {
    const path = kind === 'source' ? `/source/${id}` : `/panel/${id}`;
    if (typeof window === 'undefined') return path;
    const override = (import.meta.env.VITE_PANEL_BASE_URL as string | undefined)?.replace(/\/$/, '');
    if (override) return `${override}${path}`;
    const { protocol, hostname, port } = window.location;
    const isDev =
      hostname === 'localhost' || hostname === '127.0.0.1' || /^192\.168\./.test(hostname);
    if (isDev) return `${protocol}//${hostname}:5174${path}`;
    const panelHost = hostname.startsWith('panel.') ? hostname : `panel.${hostname}`;
    const portPart = port && port !== '80' && port !== '443' ? `:${port}` : '';
    return `${protocol}//${panelHost}${portPart}${path}`;
  }
  const [addBtn, setAddBtn] = useState<AddBtnState>(null);
  const [menu, setMenu] = useState<MenuState>(null);

  // Zwei Ansichts-Slots: welches Panel ist links/rechts sichtbar + ob Remote-Modus.
  const [slotA, setSlotA] = useState<string | null>(null);
  const [slotB, setSlotB] = useState<string | null>(null);
  const [remoteA, setRemoteA] = useState(false);
  const [remoteB, setRemoteB] = useState(false);
  const [overviewCopied, setOverviewCopied] = useState(false);

  // Übersicht-Link (alle Panels). Bewusst nur HIER im Admin – Nutzer landen
  // sonst nie auf der Auswahl, sie öffnen ihren direkten /panel/:id-Link.
  const overviewUrl = (() => {
    if (typeof window === 'undefined') return '';
    const override = (import.meta.env.VITE_PANEL_BASE_URL as string | undefined)?.replace(/\/$/, '');
    if (override) return `${override}/overview`;
    const { protocol, hostname, port } = window.location;
    const isDev =
      hostname === 'localhost' || hostname === '127.0.0.1' || /^192\.168\./.test(hostname);
    if (isDev) return `${protocol}//${hostname}:5174/overview`;
    const panelHost = hostname.startsWith('panel.') ? hostname : `panel.${hostname}`;
    const portPart = port && port !== '80' && port !== '443' ? `:${port}` : '';
    return `${protocol}//${panelHost}${portPart}/overview`;
  })();

  async function copyOverview(): Promise<void> {
    try {
      await navigator.clipboard.writeText(overviewUrl);
      setOverviewCopied(true);
      setTimeout(() => setOverviewCopied(false), 1800);
    } catch {
      window.prompt('Übersicht-Link kopieren:', overviewUrl);
    }
  }

  // Beim ersten Laden die ersten zwei Panels in die Slots legen; gelöschte
  // Panels aus den Slots entfernen.
  const panels = draft?.panels ?? [];
  useEffect(() => {
    const ids = new Set(panels.map((p) => p.id));
    setSlotA((cur) => (cur && ids.has(cur) ? cur : panels[0]?.id ?? null));
    setSlotB((cur) => (cur && ids.has(cur) ? cur : panels[1]?.id ?? null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [panels.map((p) => p.id).join(',')]);

  if (!draft) {
    return <div className="p-6 text-text-dim">Lade Konfiguration…</div>;
  }

  // Standard-Panels (mit Tasten) vs. Quellen (Web-Einspielungen) trennen.
  const stdPanels = draft.panels.filter((p) => p.kind !== 'source');
  const srcPanels = draft.panels.filter((p) => p.kind === 'source');

  async function deleteButton(panelId: string, buttonId: string): Promise<void> {
    if (!draft) return;
    const panel = draft.panels.find((p) => p.id === panelId);
    if (!panel) return;
    const layout = panel.layout.map((s) =>
      s.buttonId === buttonId ? { ...s, buttonId: null } : s,
    );
    await api.put(`/api/v1/panels/${panel.id}`, { layout });
    await api.delete(`/api/v1/buttons/${buttonId}`);
    await loadDraft();
  }

  /**
   * Legt für einen frisch angelegten Intercom-Button „A → B" automatisch das
   * Gegenstück „B → A" auf dem Ziel-Panel an (erster freier Slot). So bekommt
   * jede gerichtete Verbindung sofort ihren Rückkanal – B kann A direkt
   * zurückrufen bzw. sieht, dass A mit ihm sprechen kann.
   *
   * No-op, wenn das Gegenstück bereits existiert. Hat das Ziel-Panel keinen
   * freien Slot, bleibt A→B bestehen und es gibt nur einen Hinweis.
   */
  async function ensureIntercomCounterpart(src: IcButton): Promise<void> {
    if (!draft) return;
    if (src.type !== 'intercom') return;
    const targetPanel = draft.panels.find((p) => p.id === src.targetPanelId);
    if (!targetPanel) return;
    const already = draft.buttons.some(
      (b) =>
        b.type === 'intercom' &&
        b.sourcePanelId === src.targetPanelId &&
        b.targetPanelId === src.sourcePanelId,
    );
    if (already) return;
    const free = targetPanel.layout.find((s) => s.buttonId === null);
    if (!free) {
      alert(
        `Gegenstück nicht angelegt: Port „${targetPanel.name}" hat keinen freien Slot.\n` +
          `„${targetPanel.name}" kann dich daher nicht direkt zurückrufen.`,
      );
      return;
    }
    const cp = await api.post<IcButton>('/api/v1/buttons', {
      type: 'intercom',
      sourcePanelId: src.targetPanelId,
      targetPanelId: src.sourcePanelId,
      mode: src.mode,
    });
    try {
      const layout = targetPanel.layout.map((s) =>
        s.position === free.position ? { ...s, buttonId: cp.id } : s,
      );
      await api.put(`/api/v1/panels/${targetPanel.id}`, { layout });
    } catch (err) {
      // Slot-Zuweisung fehlgeschlagen → verwaisten Button wieder entfernen,
      // sonst blockiert der „already"-Check künftig ein neues Gegenstück.
      await api.delete(`/api/v1/buttons/${cp.id}`).catch(() => {});
      throw err;
    }
  }

  /** Hängt einen weiteren leeren Slot an ein Panel an, damit mehr als die
   *  anfänglichen Verbindungen (Buttons) Platz finden. */
  async function addSlot(panelId: string): Promise<void> {
    if (!draft) return;
    const panel = draft.panels.find((p) => p.id === panelId);
    if (!panel) return;
    const nextPos =
      panel.layout.reduce((max, s) => Math.max(max, s.position), -1) + 1;
    const layout = [...panel.layout, { position: nextPos, buttonId: null }];
    await api.put(`/api/v1/panels/${panelId}`, { layout });
    await loadDraft();
  }

  async function renamePort(panelId: string): Promise<void> {
    if (!draft) return;
    const panel = draft.panels.find((p) => p.id === panelId);
    if (!panel) return;
    const next = window.prompt('Neuer Port-Name:', panel.name);
    if (next === null) return;
    const trimmed = next.trim();
    if (!trimmed || trimmed === panel.name) return;
    await api.put(`/api/v1/panels/${panel.id}`, { name: trimmed });
    await loadDraft();
  }

  /** Button innerhalb eines Panels auf eine andere Slot-Position schieben
   *  (tauscht mit dem evtl. dort liegenden Button). Stammt der Button aus
   *  einem ANDEREN Panel, wird er stattdessen auf die Position dupliziert. */
  async function moveButtonToSlot(
    panelId: string,
    buttonId: string,
    targetPosition: number,
  ): Promise<void> {
    if (!draft) return;
    const panel = draft.panels.find((p) => p.id === panelId);
    if (!panel) return;
    const fromSlot = panel.layout.find((s) => s.buttonId === buttonId);
    if (!fromSlot) {
      // Button gehört zu einem anderen Panel → an Zielposition duplizieren.
      try {
        await duplicateButtonInto(buttonId, panelId, targetPosition);
      } catch (err) {
        alert(`Verschieben fehlgeschlagen: ${(err as Error).message}`);
      }
      return;
    }
    if (fromSlot.position === targetPosition) return;
    const targetSlot = panel.layout.find((s) => s.position === targetPosition);
    const targetButtonId = targetSlot?.buttonId ?? null;
    const layout = panel.layout.map((s) => {
      if (s.position === targetPosition) return { ...s, buttonId };
      if (s.position === fromSlot.position) return { ...s, buttonId: targetButtonId };
      return s;
    });
    await api.put(`/api/v1/panels/${panelId}`, { layout });
    await loadDraft();
  }

  async function duplicateButtonInto(
    sourceButtonId: string,
    targetPanelId: string,
    targetPosition?: number,
  ): Promise<void> {
    if (!draft) return;
    const target = draft.panels.find((p) => p.id === targetPanelId);
    if (!target) return;
    const slot =
      typeof targetPosition === 'number'
        ? target.layout.find((s) => s.position === targetPosition)
        : target.layout.find((s) => !s.buttonId);
    if (!slot) {
      alert(`Port "${target.name}" hat keinen freien Slot.`);
      return;
    }
    if (slot.buttonId) {
      alert('Slot ist bereits belegt.');
      return;
    }
    const created = await api.post<IcButton>(
      `/api/v1/buttons/${sourceButtonId}/duplicate-to/${target.id}`,
    );
    const layout = target.layout.map((s) =>
      s.position === slot.position ? { ...s, buttonId: created.id } : s,
    );
    await api.put(`/api/v1/panels/${target.id}`, { layout });
    await loadDraft();
  }

  function buttonMenuItems(panelId: string, buttonId: string): ContextMenuItem[] {
    const btn = draft!.buttons.find((b) => b.id === buttonId);
    if (!btn) return [];
    const otherPanels = draft!.panels.filter((p) => p.id !== panelId);
    const currentMode = 'mode' in btn ? btn.mode : undefined;
    return [
      {
        label: 'Beschriftung ändern',
        icon: <Pencil size={12} />,
        onSelect: async () => {
          const current = btn.labelOverride ?? '';
          const next = window.prompt(
            'Button-Text (leer = Name des Ziel-Ports automatisch):',
            current,
          );
          if (next === null) return;
          const trimmed = next.trim();
          await api.put(`/api/v1/buttons/${buttonId}`, {
            labelOverride: trimmed.length === 0 ? null : trimmed,
          });
          await loadDraft();
        },
      },
      ...(currentMode
        ? [
            {
              label: 'Modus',
              icon: <RadioIcon size={12} />,
              submenu: [
                {
                  label: `${currentMode === 'ptt' ? '● ' : '   '}Push-to-Talk`,
                  onSelect: async () => {
                    await api.put(`/api/v1/buttons/${buttonId}`, { mode: 'ptt' });
                    await loadDraft();
                  },
                },
                {
                  label: `${currentMode === 'latching' ? '● ' : '   '}Latching`,
                  onSelect: async () => {
                    await api.put(`/api/v1/buttons/${buttonId}`, { mode: 'latching' });
                    await loadDraft();
                  },
                },
              ],
            } satisfies ContextMenuItem,
          ]
        : []),
      {
        label: 'Bearbeiten…',
        icon: <Sliders size={12} />,
        onSelect: () => setAddBtn({ mode: 'edit', button: btn }),
      },
      {
        label: 'Kopieren',
        icon: <Copy size={12} />,
        onSelect: () => copyToClipboard(btn),
      },
      {
        label: 'Kopieren nach …',
        icon: <ClipboardPaste size={12} />,
        disabled: otherPanels.length === 0,
        submenu: otherPanels.map((p) => ({
          label: p.name,
          onSelect: () => duplicateButtonInto(buttonId, p.id),
        })),
      },
      {
        separator: true,
        label: 'Löschen',
        icon: <Trash2 size={12} />,
        destructive: true,
        onSelect: async () => {
          const label = btn.labelOverride ?? buttonId;
          if (!confirm(`Button "${label}" wirklich löschen?`)) return;
          await deleteButton(panelId, buttonId);
        },
      },
    ];
  }

  function slotMenuItems(panelId: string, position: number): ContextMenuItem[] {
    const newSub: ContextMenuItem[] = [
      {
        label: 'Call to Port',
        icon: <RadioIcon size={12} />,
        onSelect: () =>
          setAddBtn({ mode: 'create', panelId, position, initialType: 'intercom' }),
      },
      {
        label: 'Port-Gruppe',
        icon: <Users size={12} />,
        onSelect: () =>
          setAddBtn({ mode: 'create', panelId, position, initialType: 'partyline' }),
      },
      {
        label: 'Listen to Port',
        icon: <Headphones size={12} />,
        onSelect: () =>
          setAddBtn({ mode: 'create', panelId, position, initialType: 'listen' }),
      },
      {
        label: 'Call to all',
        icon: <Megaphone size={12} />,
        onSelect: () =>
          setAddBtn({ mode: 'create', panelId, position, initialType: 'call-to-all' }),
      },
      {
        label: 'External Trigger',
        icon: <Cable size={12} />,
        onSelect: () =>
          setAddBtn({ mode: 'create', panelId, position, initialType: 'external' }),
      },
    ];
    const otherPanels = draft!.panels.filter((p) => p.id !== panelId);
    return [
      { label: 'Neuer Button', icon: <Plus size={12} />, submenu: newSub },
      {
        separator: true,
        label: 'Einfügen',
        icon: <ClipboardPaste size={12} />,
        disabled: !clipboardButton,
        onSelect: () =>
          clipboardButton ? duplicateButtonInto(clipboardButton.id, panelId, position) : undefined,
      },
      {
        label: 'Einfügen in anderes Panel …',
        icon: <ClipboardPaste size={12} />,
        disabled: !clipboardButton || otherPanels.length === 0,
        submenu: otherPanels.map((p) => ({
          label: p.name,
          onSelect: () =>
            clipboardButton ? duplicateButtonInto(clipboardButton.id, p.id) : undefined,
        })),
      },
    ];
  }

  const menuItems: ContextMenuItem[] = menu
    ? menu.kind === 'button'
      ? buttonMenuItems(menu.panelId, menu.buttonId)
      : slotMenuItems(menu.panelId, menu.position)
    : [];

  /** Panel in einen freien (oder den linken) Ansichts-Slot laden. */
  function openInSlot(panelId: string): void {
    if (slotA === panelId || slotB === panelId) return; // schon offen
    if (!slotA) setSlotA(panelId);
    else if (!slotB) setSlotB(panelId);
    else setSlotA(panelId); // beide belegt → links ersetzen
    select('panel', panelId);
  }

  const sharedSlotProps = {
    draft,
    live,
    online,
    pressed,
    togglePress,
    deleteButton,
    renamePort,
    addSlot,
    duplicateButtonInto,
    moveButtonToSlot,
    loadDraft,
    select,
    setAddBtn,
    setMenu,
    pairingByPanelId,
    onRegeneratePairing: regeneratePairing,
    remoteVol,
    setButtonVolume,
  };

  return (
    <div className="p-6 space-y-5">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-text">Ports verwalten</h1>
          <p className="text-sm text-text-dim mt-1">
            Zwei Ports gleichzeitig bearbeiten oder fernsteuern. {draft.panels.length} Port
            {draft.panels.length === 1 ? '' : 's'} im Draft.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={copyOverview}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-btn border border-border text-xs text-text-mute hover:text-accent hover:border-accent transition"
            title={`Übersicht-Link (alle Panels) – nur zum Teilen: ${overviewUrl}`}
          >
            {overviewCopied ? <Check size={14} /> : <Link2 size={14} />}
            {overviewCopied ? 'kopiert!' : 'Übersicht-Link'}
          </button>
          <Button
            variant="primary"
            size="sm"
            leftIcon={<Plus size={14} />}
            onClick={() => setCreateOpen('standard')}
          >
            Neuer Port
          </Button>
        </div>
      </header>

      {draft.panels.length === 0 ? (
        <EmptyState
          icon={<Grid3x3 size={32} />}
          title="Noch keine Ports"
          description="Lege einen ersten Port an. Er wird mit 12 leeren Slots initialisiert."
          action={
            <Button
              variant="primary"
              size="sm"
              leftIcon={<Plus size={14} />}
              onClick={() => setCreateOpen('standard')}
            >
              Ersten Port anlegen
            </Button>
          }
        />
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_300px] gap-5">
          {/* Zwei große Panel-Ansichten UNTEREINANDER */}
          <div className="grid grid-cols-1 gap-6">
            <SlotView
              slotPanelId={slotA}
              onPick={setSlotA}
              remote={remoteA}
              onToggleRemote={() => setRemoteA((v) => !v)}
              {...sharedSlotProps}
            />
            <SlotView
              slotPanelId={slotB}
              onPick={setSlotB}
              remote={remoteB}
              onToggleRemote={() => setRemoteB((v) => !v)}
              {...sharedSlotProps}
            />
          </div>

          {/* Rechte Liste: Standard-Panels + Quellen getrennt */}
          <aside className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <Button
                variant="primary"
                size="sm"
                leftIcon={<Plus size={14} />}
                onClick={() => setCreateOpen('standard')}
                className="w-full"
              >
                Panel
              </Button>
              <Button
                variant="ghost"
                size="sm"
                leftIcon={<RadioIcon size={14} />}
                onClick={() => setCreateOpen('source')}
                className="w-full"
              >
                Quelle
              </Button>
            </div>

            <div className="text-xxs uppercase tracking-wider text-text-mute">
              Panels ({stdPanels.length}) · Doppelklick öffnet
            </div>
            <ul className="space-y-1.5">
              {stdPanels.map((p) => {
                const isOpen = slotA === p.id || slotB === p.id;
                const occ = p.layout.filter((s) => s.buttonId).length;
                return (
                  <li key={p.id}>
                    <button
                      type="button"
                      onDoubleClick={() => openInSlot(p.id)}
                      onClick={() => select('panel', p.id)}
                      className={[
                        'w-full text-left px-3 py-2 rounded-md border transition flex items-center justify-between gap-2',
                        isOpen
                          ? 'border-accent bg-[#101820] text-text'
                          : 'border-border bg-surface hover:bg-hover text-text-dim',
                      ].join(' ')}
                      title="Doppelklick: in der Ansicht öffnen"
                    >
                      <span className="truncate font-medium">{p.name}</span>
                      <span className="text-xxs text-text-mute shrink-0">{occ}/{p.layout.length}</span>
                    </button>
                  </li>
                );
              })}
            </ul>

            {/* Quellen: eigene Web-Einspielungen, je mit Öffnen-Link */}
            <div className="text-xxs uppercase tracking-wider text-text-mute pt-2">
              Quellen ({srcPanels.length})
            </div>
            {srcPanels.length === 0 ? (
              <p className="text-xxs text-text-mute">
                Noch keine Quelle. „Quelle" anlegen → Link öffnen → Audio senden.
              </p>
            ) : (
              <ul className="space-y-1.5">
                {srcPanels.map((p) => (
                  <li
                    key={p.id}
                    className="px-3 py-2 rounded-md border border-border bg-surface flex items-center justify-between gap-2"
                  >
                    <span className="truncate font-medium text-text flex items-center gap-1.5">
                      <RadioIcon size={12} className="text-accent shrink-0" />
                      {p.name}
                    </span>
                    <span className="flex items-center gap-1 shrink-0">
                      <a
                        href={panelOpenUrl(p.id, 'source')}
                        target="_blank"
                        rel="noreferrer"
                        className="text-xxs uppercase tracking-wider text-text-mute hover:text-accent"
                        title="Quelle-Seite öffnen"
                      >
                        Öffnen
                      </a>
                      <button
                        type="button"
                        onClick={() => void navigator.clipboard.writeText(panelOpenUrl(p.id, 'source'))}
                        className="text-text-mute hover:text-accent"
                        title="Link kopieren"
                      >
                        <Link2 size={12} />
                      </button>
                      <button
                        type="button"
                        onClick={async () => {
                          if (!confirm(`Quelle "${p.name}" löschen?`)) return;
                          await api.delete(`/api/v1/panels/${p.id}`);
                          await loadDraft();
                        }}
                        className="text-text-mute hover:text-err"
                        title="Löschen"
                      >
                        <Trash2 size={12} />
                      </button>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </aside>
        </div>
      )}

      <CreatePanelModal
        open={createOpen !== null}
        kind={createOpen ?? 'standard'}
        onClose={() => setCreateOpen(null)}
        onCreated={async (createdId, createdKind) => {
          setCreateOpen(null);
          await loadDraft();
          await fetchPairing();
          // Quellen nicht in die A/B-Editieransicht öffnen (haben keine Tasten).
          if (createdId && createdKind !== 'source') openInSlot(createdId);
        }}
      />

      <AddButtonModal
        state={addBtn}
        draft={draft}
        onClose={() => setAddBtn(null)}
        onSaved={async (saved) => {
          if (addBtn?.mode === 'create') {
            const panel = draft.panels.find((p) => p.id === addBtn.panelId);
            if (panel) {
              const layout = panel.layout.map((s) =>
                s.position === addBtn.position ? { ...s, buttonId: saved.id } : s,
              );
              await api.put(`/api/v1/panels/${panel.id}`, { layout });
            }
            // Bidirektional: „Call to Port"-Button bekommt automatisch sein
            // Gegenstück auf dem Ziel-Panel (Rückkanal).
            await ensureIntercomCounterpart(saved);
          }
          setAddBtn(null);
          await loadDraft();
        }}
      />

      {menu ? (
        <ContextMenu
          position={{ x: menu.x, y: menu.y }}
          items={menuItems}
          onClose={() => setMenu(null)}
        />
      ) : null}
    </div>
  );
}

/* ============================================================
   Eine der zwei großen Panel-Ansichten (Edit- oder Remote-Modus)
   ============================================================ */
type SlotShared = {
  draft: NonNullable<ReturnType<typeof useConfigStore.getState>['draft']>;
  live: ReturnType<typeof useConfigStore.getState>['live'];
  online: ReadonlySet<string>;
  pressed: Record<string, ReadonlySet<string>>;
  togglePress: (panelId: string, buttonId: string) => void;
  deleteButton: (panelId: string, buttonId: string) => Promise<void>;
  renamePort: (panelId: string) => Promise<void>;
  addSlot: (panelId: string) => Promise<void>;
  duplicateButtonInto: (b: string, p: string) => Promise<void>;
  moveButtonToSlot: (panelId: string, buttonId: string, position: number) => Promise<void>;
  loadDraft: () => Promise<void>;
  select: (kind: 'panel' | 'button', id: string) => void;
  setAddBtn: (s: AddBtnState) => void;
  setMenu: (m: MenuState) => void;
  pairingByPanelId: Record<string, PairingInfo>;
  onRegeneratePairing: (panelId: string) => void | Promise<void>;
  remoteVol: Record<string, number>;
  setButtonVolume: (panelId: string, buttonId: string, volume: number) => void;
};

/** Fügt eine Leerstelle in der Mitte ein: "123456" → "123 456" (Lesbarkeit). */
function formatPairingCode(code: string): string {
  return code.length === 6 ? `${code.slice(0, 3)} ${code.slice(3)}` : code;
}

function SlotView(
  props: SlotShared & {
    slotPanelId: string | null;
    onPick: (id: string | null) => void;
    remote: boolean;
    onToggleRemote: () => void;
  },
): JSX.Element {
  const { draft, live, online, pressed, togglePress, slotPanelId, onPick, remote, onToggleRemote } =
    props;
  const [copied, setCopied] = useState(false);
  const [codeCopied, setCodeCopied] = useState(false);

  const editPanel = draft.panels.find((p) => p.id === slotPanelId) ?? null;
  const livePanel = live?.panels.find((p) => p.id === slotPanelId) ?? null;
  const pairing = editPanel ? props.pairingByPanelId[editPanel.id] : undefined;

  const panelUrl = (() => {
    if (typeof window === 'undefined' || !slotPanelId) return '';
    const override = (import.meta.env.VITE_PANEL_BASE_URL as string | undefined)?.replace(/\/$/, '');
    if (override) return `${override}/panel/${slotPanelId}`;
    const { protocol, hostname, port } = window.location;
    const isDev = hostname === 'localhost' || hostname === '127.0.0.1' || /^192\.168\./.test(hostname);
    if (isDev) return `${protocol}//${hostname}:5174/panel/${slotPanelId}`;
    const panelHost = hostname.startsWith('panel.') ? hostname : `panel.${hostname}`;
    const portPart = port && port !== '80' && port !== '443' ? `:${port}` : '';
    return `${protocol}//${panelHost}${portPart}/panel/${slotPanelId}`;
  })();

  async function copyLink(): Promise<void> {
    try {
      await navigator.clipboard.writeText(panelUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      window.prompt('Panel-Link kopieren:', panelUrl);
    }
  }

  async function copyCode(): Promise<void> {
    if (!pairing) return;
    try {
      await navigator.clipboard.writeText(pairing.pairingCode);
      setCodeCopied(true);
      setTimeout(() => setCodeCopied(false), 1800);
    } catch {
      window.prompt('Pairing-Code kopieren:', pairing.pairingCode);
    }
  }

  return (
    <div className="space-y-2 min-w-0">
      {/* Auswahl-Dropdown + Remote-Umschalter */}
      <div className="flex items-center gap-2">
        <select
          className="input flex-1"
          value={slotPanelId ?? ''}
          onChange={(e) => onPick(e.target.value || null)}
        >
          <option value="">— Port wählen —</option>
          {draft.panels
            .filter((p) => p.kind !== 'source')
            .map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
        </select>
        <Button
          variant={remote ? 'primary' : 'ghost'}
          size="sm"
          leftIcon={<Gamepad2 size={14} />}
          onClick={onToggleRemote}
          title={remote ? 'Remote-Modus aktiv – klicken zum Bearbeiten' : 'In Remote-Modus wechseln'}
          disabled={!slotPanelId}
        >
          Remote
        </Button>
      </div>

      {!editPanel ? (
        <div className="panel-shell p-8 text-center text-sm text-text-mute">
          Wähle oben einen Port oder doppelklicke rechts in der Liste.
        </div>
      ) : remote ? (
        // ---- REMOTE-MODUS: Live-Spiegel, Klick = Press/Release ----
        !livePanel ? (
          <div className="panel-shell p-8 text-center text-sm text-text-mute">
            „{editPanel.name}" ist noch nicht live. Erst <strong>Push</strong> drücken, dann
            kann es ferngesteuert werden.
          </div>
        ) : (
          // Im Remote-Modus das GANZE Panel farblich umranden.
          <div className="panel-bigger rounded-panel ring-2 ring-[#ef4444]">
            <PanelCard
              panel={livePanel}
              draft={live!}
              online={online.has(livePanel.id)}
              selectedButtonId={firstPressed(pressed[livePanel.id])}
              receivingButtonIds={computeReceiving(live, pressed, livePanel.id)}
              onButtonClick={(buttonId) => togglePress(livePanel.id, buttonId)}
              buttonVolumes={props.remoteVol}
              onButtonVolumeChange={(buttonId, volume) =>
                props.setButtonVolume(livePanel.id, buttonId, volume)
              }
            />
          </div>
        )
      ) : (
        // ---- EDIT-MODUS: Buttons anlegen/bearbeiten ----
        <div className="panel-bigger">
          <PanelCard
            panel={editPanel}
            draft={draft}
            onSelect={() => props.select('panel', editPanel.id)}
            onAddSlot={(position) => props.setAddBtn({ mode: 'create', panelId: editPanel.id, position })}
            onButtonClick={(buttonId) => props.select('button', buttonId)}
            draggableButtons
            onMoveToSlot={(buttonId, position) =>
              props.moveButtonToSlot(editPanel.id, buttonId, position)
            }
            onButtonDragOut={async (buttonId) => {
              const btn = draft.buttons.find((b) => b.id === buttonId);
              const label = btn?.labelOverride ?? buttonId;
              if (!confirm(`Button "${label}" wirklich löschen?`)) return;
              await props.deleteButton(editPanel.id, buttonId);
            }}
            onButtonContextMenu={(buttonId, pos) =>
              props.setMenu({ kind: 'button', panelId: editPanel.id, buttonId, x: pos.x, y: pos.y })
            }
            onEmptyContextMenu={(position, pos) =>
              props.setMenu({ kind: 'slot', panelId: editPanel.id, position, x: pos.x, y: pos.y })
            }
            onDropButton={async (buttonId) => {
              if (editPanel.layout.some((s) => s.buttonId === buttonId)) return;
              try {
                await props.duplicateButtonInto(buttonId, editPanel.id);
              } catch (err) {
                alert(`Drop fehlgeschlagen: ${(err as Error).message}`);
              }
            }}
          />
        </div>
      )}

      {/* Toolbar unter dem Panel */}
      {editPanel ? (
        <div className="flex justify-between items-center gap-2">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={copyLink}
              className="flex items-center gap-1.5 text-xxs uppercase tracking-wider text-text-mute hover:text-accent transition"
              title={panelUrl}
            >
              {copied ? <Check size={12} /> : <Link2 size={12} />}
              {copied ? 'kopiert!' : 'Share-Link'}
            </button>
            {pairing?.paired ? (
              <div className="flex items-center gap-1.5">
                <span className="flex items-center gap-1 text-xxs uppercase tracking-wider text-ok">
                  <Check size={12} />
                  Gepaart{pairing.pairedAt ? ` · ${new Date(pairing.pairedAt).toLocaleString()}` : ''}
                </span>
                <button
                  type="button"
                  onClick={() => void props.onRegeneratePairing(editPanel.id)}
                  className="text-text-mute hover:text-accent transition"
                  title="Code neu erzeugen (koppelt das aktuelle iOS-Gerät ab)"
                >
                  <RefreshCw size={12} />
                </button>
              </div>
            ) : pairing ? (
              <button
                type="button"
                onClick={copyCode}
                className="flex items-center gap-1.5 text-xxs uppercase tracking-wider text-text-mute hover:text-accent transition"
                title="Pairing-Code für die iOS-App kopieren"
              >
                {codeCopied ? <Check size={12} /> : <KeyRound size={12} />}
                {codeCopied ? 'kopiert!' : `Code: ${formatPairingCode(pairing.pairingCode)}`}
              </button>
            ) : null}
          </div>
          <div className="flex items-center gap-2">
            {!remote ? (
              <Button
                variant="ghost"
                size="sm"
                leftIcon={<Plus size={12} />}
                onClick={() => void props.addSlot(editPanel.id)}
                title="Einen weiteren Slot anhängen (mehr Verbindungen)"
              >
                Slot
              </Button>
            ) : null}
            <Button
              variant={editPanel.admin ? 'primary' : 'ghost'}
              size="sm"
              leftIcon={<ShieldCheck size={12} />}
              onClick={async () => {
                await api.put(`/api/v1/panels/${editPanel.id}`, { admin: !editPanel.admin });
                await props.loadDraft();
              }}
              title="Adminrechte: dieses Panel zeigt in der App einen „Admin“-Button (Serverauslastung + Panel-Bearbeitung im eingebetteten Backend)"
            >
              Admin
            </Button>
            <Button variant="ghost" size="sm" leftIcon={<Pencil size={12} />} onClick={() => props.renamePort(editPanel.id)}>
              Umbenennen
            </Button>
            <Button
              variant="ghost"
              size="sm"
              leftIcon={<Trash2 size={12} />}
              onClick={async () => {
                if (!confirm(`Port "${editPanel.name}" wirklich löschen?`)) return;
                await api.delete(`/api/v1/panels/${editPanel.id}`);
                await props.loadDraft();
              }}
            >
              Löschen
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function CreatePanelModal({
  open,
  kind,
  onClose,
  onCreated,
}: {
  open: boolean;
  kind: 'standard' | 'source';
  onClose: () => void;
  onCreated: (createdId?: string, createdKind?: 'standard' | 'source') => Promise<void>;
}): JSX.Element {
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const isSource = kind === 'source';

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isSource ? 'Neue Quelle' : 'Neuer Port'}
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onClose} disabled={busy}>
            Abbrechen
          </Button>
          <Button
            variant="primary"
            size="sm"
            disabled={!name.trim() || busy}
            onClick={async () => {
              setBusy(true);
              try {
                const created = await api.post<Panel>('/api/v1/panels', {
                  name: name.trim(),
                  ...(isSource ? { kind: 'source' } : {}),
                });
                setName('');
                await onCreated(created?.id, kind);
              } finally {
                setBusy(false);
              }
            }}
          >
            Anlegen
          </Button>
        </>
      }
    >
      <Field
        label={isSource ? 'Quelle-Name' : 'Port-Name'}
        hint={
          isSource
            ? 'Erscheint als Port-Name; öffne danach den Quelle-Link und sende Audio.'
            : 'Wird auf anderen Ports als Button-Label angezeigt.'
        }
      >
        <input
          autoFocus
          className="input"
          placeholder={isSource ? 'z. B. PGM Regie' : 'z. B. Regie 1'}
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </Field>
    </Modal>
  );
}

function AddButtonModal({
  state,
  draft,
  onClose,
  onSaved,
}: {
  state: AddBtnState;
  draft: NonNullable<ReturnType<typeof useConfigStore.getState>['draft']>;
  onClose: () => void;
  onSaved: (saved: IcButton) => Promise<void>;
}): JSX.Element {
  const editing = state?.mode === 'edit' ? state.button : null;
  const defaultType: IcButton['type'] =
    editing?.type ?? (state?.mode === 'create' ? state.initialType ?? 'intercom' : 'intercom');
  const livePorts = useExternalsStore((s) => s.ports);

  const [type, setType] = useState<IcButton['type']>(defaultType);
  const [targetPanelId, setTargetPanelId] = useState(
    editing && 'targetPanelId' in editing ? editing.targetPanelId ?? '' : '',
  );
  const [partylineId, setPartylineId] = useState(
    editing && 'partylineId' in editing ? editing.partylineId ?? '' : '',
  );
  const [externalId, setExternalId] = useState(editing?.type === 'external' ? editing.externalId : '');
  const [portKey, setPortKey] = useState(editing?.type === 'external-port' ? editing.portKey : '');
  const [externalDeviceId, setExternalDeviceId] = useState(
    editing?.type === 'external-device' ? editing.externalDeviceId : '',
  );
  const [mode, setMode] = useState<ButtonMode>(
    editing && 'mode' in editing
      ? editing.mode
      : defaultType === 'listen' || defaultType === 'external-port'
      ? 'latching'
      : 'ptt',
  );
  const [labelOverride, setLabelOverride] = useState<string>(editing?.labelOverride ?? '');
  const [busy, setBusy] = useState(false);

  const stateKey = state
    ? state.mode === 'edit'
      ? `edit:${state.button.id}`
      : `create:${state.panelId}:${state.position}:${state.initialType ?? ''}`
    : '';
  const [lastKey, setLastKey] = useState(stateKey);
  if (state && lastKey !== stateKey) {
    setLastKey(stateKey);
    setType(defaultType);
    setTargetPanelId(editing && 'targetPanelId' in editing ? editing.targetPanelId ?? '' : '');
    setPartylineId(editing && 'partylineId' in editing ? editing.partylineId ?? '' : '');
    setExternalId(editing?.type === 'external' ? editing.externalId : '');
    setPortKey(editing?.type === 'external-port' ? editing.portKey : '');
    setExternalDeviceId(editing?.type === 'external-device' ? editing.externalDeviceId : '');
    setMode(
      editing && 'mode' in editing
        ? editing.mode
        : defaultType === 'listen' || defaultType === 'external-port'
        ? 'latching'
        : 'ptt',
    );
    setLabelOverride(editing?.labelOverride ?? '');
  }

  const sourcePanel = state
    ? state.mode === 'edit'
      ? draft.panels.find((p) => p.id === state.button.sourcePanelId)
      : draft.panels.find((p) => p.id === state.panelId)
    : null;
  const sourcePanelId = state
    ? state.mode === 'edit'
      ? state.button.sourcePanelId
      : state.panelId
    : '';
  // Quellen sind keine Gesprächsziele (man hört sie via „Listen Port").
  const candidates = draft.panels.filter(
    (p) => p.id !== sourcePanelId && p.kind !== 'source',
  );

  if (!state || !sourcePanel) {
    return (
      <Modal open={false} onClose={onClose} title="">
        <></>
      </Modal>
    );
  }

  const isEdit = state.mode === 'edit';
  const title = isEdit
    ? `Button bearbeiten · ${sourcePanel.name}`
    : `Button hinzufügen → ${sourcePanel.name} · Slot ${state.position + 1}`;

  // Standard-Beschriftung = Name des Ziel-Ports / der Port-Gruppe.
  const targetName =
    draft.panels.find((p) => p.id === targetPanelId)?.name ??
    draft.partylines.find((p) => p.id === partylineId)?.name ??
    '';
  const labelPlaceholder = targetName || 'automatisch';

  return (
    <Modal
      open
      onClose={onClose}
      title={title}
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onClose} disabled={busy}>
            Abbrechen
          </Button>
          <Button
            variant="primary"
            size="sm"
            disabled={busy || !isValid()}
            onClick={async () => {
              setBusy(true);
              try {
                const saved = await save();
                await onSaved(saved);
              } finally {
                setBusy(false);
              }
            }}
          >
            {isEdit ? 'Speichern' : 'Hinzufügen'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label="Typ">
          <select
            className="input"
            value={type}
            disabled={isEdit}
            onChange={(e) => {
              const next = e.target.value as IcButton['type'];
              setType(next);
              if (next === 'external-port') setMode('latching');
            }}
          >
            <option value="intercom">Call to Port</option>
            <option value="partyline">Port-Gruppe</option>
            <option value="listen">Listen to Port</option>
            <option value="call-to-all">Call to all Ports</option>
            <option value="external">External Trigger</option>
            <option value="external-port">Ext. Port (Listen)</option>
            <option value="external-device">Ext. Gerät (bidi)</option>
          </select>
        </Field>

        {(type === 'intercom' || type === 'listen') && (
          <Field label="Ziel-Port">
            <select className="input" value={targetPanelId} onChange={(e) => setTargetPanelId(e.target.value)}>
              <option value="">— wählen —</option>
              {candidates.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </Field>
        )}

        {(type === 'partyline' || type === 'listen') && (
          <Field label="Port-Gruppe">
            <select className="input" value={partylineId} onChange={(e) => setPartylineId(e.target.value)}>
              <option value="">— wählen —</option>
              {draft.partylines.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </Field>
        )}

        {type === 'external' && (
          <Field label="External ID" hint="z. B. webhook-name oder Tally-Trigger">
            <input className="input" value={externalId} onChange={(e) => setExternalId(e.target.value)} />
          </Field>
        )}

        {type === 'external-port' && (
          <Field label="Externer Port" hint="PGM / ext-Mic Quelle (Listen-only). Online-Ports werden hervorgehoben.">
            <select className="input" value={portKey} onChange={(e) => setPortKey(e.target.value)}>
              <option value="">— wählen —</option>
              {livePorts
                .filter((p) => p.kind !== 'ext-spk')
                .map((p) => (
                  <option key={p.key} value={p.key}>
                    {p.online ? '● ' : '○ '}
                    [{p.kind}] {p.label}
                  </option>
                ))}
            </select>
          </Field>
        )}

        {type === 'external-device' && (
          <Field label="Externes Gerät" hint="Gepaartes Mic+Speaker (bidirektional). Pflegen unter „Externe Geräte“.">
            <select className="input" value={externalDeviceId} onChange={(e) => setExternalDeviceId(e.target.value)}>
              <option value="">— wählen —</option>
              {(draft.externalDevices ?? []).map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>
          </Field>
        )}

        {type !== 'external-port' && (
          <Field
            label="Modus"
            hint={type === 'listen' ? 'Latching = dauerhaft mithören, PTT = nur während Druck.' : undefined}
          >
            <select className="input" value={mode} onChange={(e) => setMode(e.target.value as ButtonMode)}>
              <option value="ptt">Push-to-Talk</option>
              <option value="latching">Latching</option>
            </select>
          </Field>
        )}

        <Field
          label="Beschriftung (Button-Text)"
          hint="Leer lassen = automatisch der Name des Ziel-Ports."
        >
          <input
            className="input"
            value={labelOverride}
            placeholder={labelPlaceholder}
            onChange={(e) => setLabelOverride(e.target.value)}
          />
        </Field>
      </div>
    </Modal>
  );

  function isValid(): boolean {
    if (type === 'intercom') return Boolean(targetPanelId);
    if (type === 'partyline') return Boolean(partylineId);
    if (type === 'listen') return Boolean(targetPanelId || partylineId);
    if (type === 'external') return externalId.trim().length > 0;
    if (type === 'external-port') return portKey.trim().length > 0;
    if (type === 'external-device') return externalDeviceId.trim().length > 0;
    return true;
  }

  async function save(): Promise<IcButton> {
    if (!state) throw new Error('no state');
    const label = labelOverride.trim();
    const labelField = { labelOverride: label.length > 0 ? label : null };

    if (state.mode === 'edit') {
      const id = state.button.id;
      const body: Record<string, unknown> = { volume: state.button.volume, ...labelField };
      if (type === 'intercom') Object.assign(body, { targetPanelId, mode });
      else if (type === 'partyline') Object.assign(body, { partylineId, mode });
      else if (type === 'listen')
        Object.assign(body, { targetPanelId: targetPanelId || null, partylineId: partylineId || null, mode });
      else if (type === 'external') Object.assign(body, { externalId, mode });
      else if (type === 'external-port') Object.assign(body, { portKey, mode });
      else if (type === 'external-device') Object.assign(body, { externalDeviceId, mode });
      else Object.assign(body, { mode });
      return api.put<IcButton>(`/api/v1/buttons/${id}`, body);
    }

    const labelCreate = label.length > 0 ? { labelOverride: label } : {};
    const body =
      type === 'intercom'
        ? { type, sourcePanelId, targetPanelId, mode, ...labelCreate }
        : type === 'partyline'
        ? { type, sourcePanelId, partylineId, mode, ...labelCreate }
        : type === 'listen'
        ? {
            type,
            sourcePanelId,
            ...(targetPanelId ? { targetPanelId } : {}),
            ...(partylineId ? { partylineId } : {}),
            mode,
            ...labelCreate,
          }
        : type === 'external'
        ? { type, sourcePanelId, externalId, mode, ...labelCreate }
        : type === 'external-port'
        ? { type, sourcePanelId, portKey, mode, ...labelCreate }
        : type === 'external-device'
        ? { type, sourcePanelId, externalDeviceId, mode, ...labelCreate }
        : { type, sourcePanelId, mode, ...labelCreate };
    return api.post<IcButton>('/api/v1/buttons', body);
  }
}
